import axios from 'axios';

/**
 * Non-Resident Authentication Service
 * Handles authentication for non-residents using customerLogin API
 * Similar to VFS travel flow for document uploads and payment processing
 */
class NonResidentAuthService {
  constructor() {
    this.authToken = null;
    this.tokenExpiry = null;
    this.contentType = 'application/json';
    this.credentials = {
      email: 'integsimali@gmail.com',
      password: 'Pacistest24#',
    };
  }

  /**
   * Authenticate non-resident using customerLogin API
   * @returns {Promise<string>} Authentication token
   */
  async authenticate() {
    try {
      const apiPrefix = process.env.NODE_ENV === 'production' ? '/api' : '';
      const loginResponse = await axios.post(`${apiPrefix}/3rdparty/customerLogin`, {
        email: this.credentials.email,
        password: this.credentials.password,
      }, {
        headers: {
          'Content-Type': this.contentType,
          Accept: this.contentType,
        },
      });

      // Parse Lambda response body if it's a string (common API pattern)
      let responseData = loginResponse.data;
      if (typeof responseData === 'string') {
        responseData = JSON.parse(responseData);
      }

      // Handle different response formats
      let authToken;
      // Check for the expected format first
      if (responseData.success && responseData.data && responseData.data.authToken) {
        authToken = responseData.data.authToken;
      } else if (responseData.idToken) {
        authToken = responseData.idToken;
      } else if (responseData.statusCode === 200 && responseData.idToken) {
        authToken = responseData.idToken;
      } else {
        // console.error('API Response:', responseData);
        throw new Error(`Authentication failed: ${responseData.message || 'Unexpected response format'}`);
      }

      if (!authToken) {
        throw new Error('No authentication token received');
      }

      // Store auth token with expiry (24 hours)
      this.authToken = authToken;
      this.tokenExpiry = Date.now() + (24 * 60 * 60 * 1000);

      // console.log('Non-resident authentication successful');
      return authToken;
    } catch (error) {
      // console.error('Non-resident authentication failed:', error);
      // console.error('Error details:', error.response?.data || error.message);
      throw new Error('Failed to authenticate non-resident flow');
    }
  }

  /**
   * Get valid authentication token
   * Automatically re-authenticates if token is expired
   * @returns {Promise<string>} Valid authentication token
   */
  async getAuthToken() {
    if (this.isTokenValid()) {
      return this.authToken;
    }

    // Token expired or doesn't exist, authenticate again
    return this.authenticate();
  }

  /**
   * Check if current token is valid
   * @returns {boolean} True if token is valid and not expired
   */
  isTokenValid() {
    return this.authToken && this.tokenExpiry && Date.now() < this.tokenExpiry;
  }

  /**
   * Get authorization headers for API calls
   * @returns {Promise<object>} Headers object with authorization
   */
  async getAuthHeaders() {
    const token = await this.getAuthToken();
    return {
      Auth: token,
      'Content-Type': this.contentType,
      Accept: this.contentType,
    };
  }

  /**
   * Clear stored authentication data
   */
  clearAuth() {
    this.authToken = null;
    this.tokenExpiry = null;
  }

  /**
   * Make authenticated API call
   * @param {string} url - API endpoint URL
   * @param {object} data - Request data
   * @param {string} method - HTTP method (default: POST)
   * @returns {Promise<object>} API response
   */
  async makeAuthenticatedRequest(url, data = {}, method = 'POST') {
    const headers = await this.getAuthHeaders();

    return axios({
      method,
      url,
      data,
      headers,
    });
  }
}

// Export singleton instance
export default new NonResidentAuthService();
